/**
 * SRAB pixel-art scene palette.
 *
 * A deliberately limited, "18-bit"-feeling urban palette: cool daytime sky,
 * hazy receding skyline, warm Czech-townhouse house, muted realistic props.
 * Everything is drawn programmatically (PixiJS Graphics) at a 480×270 virtual
 * resolution, so these are the only colours the scene is allowed to use — a
 * constrained palette is what gives pixel art its coherent, non-childish feel.
 *
 * Colours are PixiJS-native `0xRRGGBB` numbers. The coverage-state accents are
 * kept in sync with the app's Tailwind `pop.*` ramp (see tailwind.config.ts)
 * so the scene speaks the same visual language as the rest of the UI.
 */

export const PALETTE = {
  // ─── Sky (top → horizon, clear late-afternoon) ───────────────────────────
  sky: [0x3b6ca8, 0x4e7fb8, 0x6593c4, 0x84aad2, 0xa8c4de, 0xccdce8],
  // Soft clouds.
  cloud: 0xeef3f7,
  cloudShadow: 0xcbd9e4,

  // ─── Night sky (top → horizon) + celestial bodies ───────────────────────
  nightSky: [0x0b1f3a, 0x122a4b, 0x1b3358, 0x274266, 0x35506f, 0x47607d],
  star: 0xeaf2ff,
  moon: 0xece7d2,
  moonShadow: 0xcdc7b0,
  moonCrater: 0xbcb59c,
  nightTint: 0x0c1b3a, // full-scene overlay at night
  lampGlow: 0xffce72, // warm street-lamp light
  lampPost: 0x2b2f37, // dark cast-iron lamp post
  wetSheen: 0x8fa6c8, // cool reflective tint on wet pavement at night

  // ─── Nature: rolling hills + forest ──────────────────────────────────────
  hillBack: 0x7d93a0,
  hillMid: 0x6e8a5c,
  hillFront: 0x577545,
  hillFrontDark: 0x46612f,
  treeConifer: 0x35592f,
  treeConiferDark: 0x264020,
  treeDecid: 0x4f7a3a,
  treeDecidDark: 0x39592a,
  treeTrunk: 0x5a4030,

  // ─── Distant shops (a Biedronka-style storefront + a grocer) ─────────────
  shopWall: 0xbcae97,
  shopWallShadow: 0x9a8d77,
  shopWindow: 0x8fb0bf,
  shopDoor: 0x5a4636,
  awningRed: 0xd11f26,
  awningRedDark: 0xa3171d,
  awningBlue: 0x2f6fae,
  awningBlueDark: 0x21527f,
  awningCream: 0xeae0cf,
  ladybug: 0xd11f26,
  ladybugSpot: 0x1f1410,
  signBoard: 0xf2ece0,

  // ─── Distant people (tiny pedestrians) ───────────────────────────────────
  crowd: [0x6b5340, 0x40566e, 0x7a4a4a, 0x4f6b3c, 0x5a5a66],
  crowdSkin: 0xc9a079,

  // ─── Character phone-scan ─────────────────────────────────────────────────
  phone: 0x222428,
  phoneScreen: 0x9fd4e8,
  phoneScreenBright: 0xdaf2fc,
  scanBeam: 0x8fe0ff,

  // ─── Skyline (atmospheric perspective: far = lighter/bluer) ──────────────
  farBuilding: 0x9fb2c6,
  farBuildingDark: 0x8ea2b9,
  midBuilding: [0x6b7e96, 0x7a6e66, 0x8a8276, 0x647189],
  midBuildingShadow: [0x55657b, 0x60564f, 0x6d665c, 0x515b6f],
  windowDark: 0x3a4452,
  windowLit: 0xe6cf8e,

  // ─── Ground / street ─────────────────────────────────────────────────────
  pavementFar: 0x9b948a,
  pavement: 0x847d73,
  pavementNear: 0x6f685f,
  pavementSeam: 0x5b554d,
  curb: 0xb0a99d,
  roadEdge: 0x57534d,
  grass: 0x5e7a4a,
  grassDark: 0x4a6238,
  hedge: 0x4f6b3c,
  hedgeDark: 0x3d5530,

  // ─── House (Czech townhouse: ochre plaster + terracotta roof) ────────────
  wallLight: 0xe7d6b8,
  wall: 0xd8c29c,
  wallShadow: 0xbfa67e,
  roofLight: 0xc4623a,
  roof: 0xa84e2e,
  roofShadow: 0x873d24,
  roofRidge: 0x6e3320,
  windowFrame: 0xf2ece0,
  windowGlass: 0x9fc4d6,
  windowGlassDark: 0x3a4654,
  doorWood: 0x6e4a2e,
  doorWoodDark: 0x523620,
  doorHandle: 0xd9b45a,
  chimney: 0xb09176,
  chimneyCap: 0x6e685f,
  foundation: 0x8a7b68,
  foundationDark: 0x6f6253,

  // ─── Car (Škoda Octavia — red liftback) ──────────────────────────────────
  carBody: 0xd2231f,
  carBodyLight: 0xe85648,
  carBodyDark: 0x9c1418,
  carGlass: 0xa9c2d2,
  carGlassDark: 0x6f93a6,
  carTrim: 0xaeb4ba,
  carTire: 0x26262b,
  carHub: 0x969ca6,
  carHeadlight: 0xf3e7ac,
  carTaillight: 0xc23a2e,
  carGrille: 0x23262b,
  carRim: 0xc2c8ce,
  carRimDark: 0x6e747a,
  carRimFace: 0x3e4147,
  carCaliper: 0xc4202a,
  carTyreInner: 0x33333a,
  carArch: 0x18181c,
  glassSheen: 0xcfe2ee,

  // ─── Suitcases (realistic luggage trio) ──────────────────────────────────
  caseTan: 0xb5663a,
  caseTanDark: 0x8a4a28,
  caseTeal: 0x356b6e,
  caseTealDark: 0x274f52,
  casePlum: 0x6e3f5e,
  casePlumDark: 0x512c45,
  caseStrap: 0x3a2e26,
  caseBuckle: 0xd9b45a,

  // ─── Person (login figure — adult, neutral) ──────────────────────────────
  skin: 0xd9a87e,
  skinShadow: 0xb98a63,
  hair: 0x3a2a20,
  coat: 0x40566e,
  coatDark: 0x32465c,
  trousers: 0x39383e,
  shoes: 0x232024,

  // ─── Neglected / "uninsured" variants (run-down, drained of life) ────────
  wallDull: 0xb3a98f,
  wallDullShadow: 0x938a72,
  roofDull: 0x8a5a44,
  roofDullLight: 0x9c6a50,
  roofDullShadow: 0x5f3d2e,
  glassGrimy: 0x44505a,
  doorWeathered: 0x5a4636,
  doorWeatheredDark: 0x3f3024,
  weed: 0x4a5e30,
  weedDark: 0x36461f,
  stain: 0x6b5d4a,
  caseTanDull: 0x8f6a4f,
  caseTealDull: 0x46595b,
  casePlumDull: 0x5c4a57,
  carDull: 0x8a4039,
  carDullLight: 0xa3564b,
  carDullDark: 0x5c2823,
  headlightOff: 0x726f56,
  taillightOff: 0x5a2a24,

  // ─── Fire + smoke (burning car) ──────────────────────────────────────────
  fireDeep: 0xb5302a,
  fireMid: 0xe8732b,
  fireHot: 0xf6cf52,
  ember: 0xf2a23a,
  smoke: 0x4a4a4a,

  // ─── Flowers + "cared-for" extras (insured) ──────────────────────────────
  flowerBox: 0x6e4a2e,
  flowerLeaf: 0x4f8a3c,
  flowerRed: 0xd9433a,
  flowerYellow: 0xf2c84b,
  flowerPink: 0xe08aa8,
  beam: 0xfff3c0,
  tag: 0xf2c84b,
  tagString: 0xe7d6b8,

  // ─── Life zone: Reaper (uninsured) ↔ Angel (insured) ─────────────────────
  reaperRobe: 0x2b2b33,
  reaperRobeDark: 0x191920,
  reaperHood: 0x0c0c10,
  reaperEye: 0xd23a2e,
  scythePole: 0x5a4030,
  scytheBlade: 0xc9d0d6,
  scytheBladeDark: 0x8a939c,
  bone: 0xe7e2d2,
  angelRobe: 0xece2cf,
  angelRobeShade: 0xc9bda6,
  angelWing: 0xf7f2e8,
  angelWingShade: 0xd7cdb8,
  angelHalo: 0xf2c84b,

  // ─── Pet zone: cat in a tree (uninsured) ↔ cat in a cradle (insured) ─────
  petTrunk: 0x5a4030,
  petTrunkDark: 0x44301f,
  petFoliage: 0x4f7a3a,
  petFoliageDark: 0x39592a,
  petFoliageLight: 0x5f9048,
  catFur: 0xcf7f3a,
  catFurDark: 0xa9682f,
  catBelly: 0xe8c9a0,
  catStripe: 0x8a4f24,
  catEar: 0xd98a8a,
  catEye: 0x9bbf4a,
  catPupil: 0x1a1a1a,
  catMouth: 0xc25b54,
  hissMark: 0xeae0cf,
  cradleWood: 0x8a6a44,
  cradleWoodDark: 0x67502f,
  cradleBlanket: 0xb8cfe0,
  cradleBlanketShade: 0x97afc4,

  // ─── Travel: aircraft (Bombardier Q400 turboprop) + runway ───────────────
  planeBody: 0xeef1f4,
  planeShade: 0xc6ccd2,
  planeWing: 0xb6bec4,
  planeWindow: 0x36424e,
  planeStripe: 0x356c8a,
  planeNose: 0xc23a2e,
  propBlade: 0x2c2c32,
  propHub: 0x55585e,
  gearStrut: 0x6a6d73,
  gearTyre: 0x222226,
  runway: 0x6b675f,
  runwayLine: 0xd9cf9e,
  stampRed: 0xc23a2e,
  stampBlue: 0x356c8a,
  stampGreen: 0x5e7a4a,

  // ─── Shared ──────────────────────────────────────────────────────────────
  shadow: 0x000000, // used at low alpha for cast shadows

  // ─── Coverage-state accents (synced with pop.* in tailwind.config.ts) ────
  covered: 0x6a8c5f,
  coveredGlow: 0xffe6a8,
  partial: 0xd9a441,
  missing: 0xc8392b,
  badgeGlyph: 0xfff4e0,
  hoverOutline: 0xf5e6c8,
} as const

/** Pick a deterministic palette entry from an array by index (wraps). */
export function pick<T>(arr: readonly T[], i: number): T {
  return arr[((i % arr.length) + arr.length) % arr.length]
}
